import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { passwordStrength } from 'check-password-strength';
import countryList from 'country-list';
import classNames from 'classnames';
import TextInput from '../../shared/FormComponents/TextInput';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import AccountForm from '../../shared/UserAccountForm';
import { withServerStyles } from '../../shared/ServerStylesConditionalWrapper';
import { compose } from '../../utils/compose';
import { device } from '../../../utils/styled-component-mixins';
import FormSelect from '../../shared/FormComponents/FormSelect';
import { STATES } from '../../../constants/states';

const RegistrationStyledWrapper = styled.div`
  .form-fields-wrapper {
    flex-wrap: wrap;
    display: flex;
    grid-column-gap: 1.6rem;
    grid-row-gap: 2.5rem;

    .form-field {
      flex: 1 1 100%;
      margin-top: 0;
      position: relative;

      @media ${device.desktopXl} {
        flex: 1 1 48%;
      }

      #password-strength {
        position: absolute;
        top: -4.5rem;
        width: max-content;
      }
    }

    .custom-checkbox {
      flex: 1 1 100%;
    }
  }

  .password-inputs {
    display: flex;
    flex-wrap: wrap;
    grid-column-gap: 1.6rem;
    grid-row-gap: 2.5rem;
    width: 100%;
  }

  .password-policy {
    h2 {
      font-size: 2rem;
    }
  }

  .form {
    box-shadow: none;
    margin: 0;
    padding: 0;
    width: auto;
  }

  .error-messages {
    margin-top: 0 !important;
  }
`;

const passwordOptions = [
  {
    id: 0,
    value: 'not-acceptable',
    minDiversity: 0,
    minLength: 8
  },
  {
    id: 1,
    value: 'average',
    minDiversity: 3,
    minLength: 8
  },
  {
    id: 2,
    value: 'strong',
    minDiversity: 3,
    minLength: 12
  },
  {
    id: 3,
    value: 'excellent',
    minDiversity: 3,
    minLength: 18
  }
];

const DEFAULT_STATE = {
  emailError: false,
  zipCodeError: false,
  password: '',
  passwordConfirmation: '',
  passwordStrength: 0,
  passwordMismatch: false,
  disableSubmit: true,
  acceptedPrivacyPolicy: false,
  isAllianceClient: false
};

const RegistrationComponent = (props) => {
  const [state, setstate] = useState(DEFAULT_STATE);
  const setState = (newState) => setstate({ ...state, ...newState });
  const [countryOptions, setCountryOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const { resourceStrings } = useLocalization();
  const recaptchaRef = useRef(null);
  const hiddenSubmitRef = useRef(null);
  const errorMessagesRef = useRef(null);
  const strengths = [
    resourceStrings['bdO.PasswordReset.PasswordStrength-not-acceptable'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-average'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-strong'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-excellent']
  ];

  const handleMatch = (event) => {
    // Compare password fields
    const comparePassword = event.target.name === 'password' ? 'passwordConfirmation' : 'password';
    return {
      passwordMismatch: event.target.value !== state[comparePassword],
      [event.target.name]: event.target.value
    };
  };

  const handlePassWordInputs = (event) => {
    let handlePassStrength = {};

    if (event.target.name === 'password') {
      handlePassStrength = {
        passwordStrength: passwordStrength(event.target.value, passwordOptions).id
      };
    }

    setState({ ...handlePassStrength, ...handleMatch(event) });
  };

  const handleZipCodeInput = (event) => {
    const zipCodeError = { zipCodeError: false };

    if (!event.target.value.match(/^[0-9]{5}$/)) {
      zipCodeError.zipCodeError = true;
    }

    setState({ ...state, zipcode: event.target.value, ...zipCodeError });
  };

  const handleEmailInput = (event) => {
    const emailError = { emailError: false };

    if (event.target.name === 'email') {
      emailError.emailError = !event.target.validity.valid;
    }

    setState({ ...state, email: event.target.value, ...emailError });
  };

  const isFormInvalid = () => {
    const inputs = document.querySelectorAll('input');
    const selects = document.querySelectorAll('select');
    const invalidInputs = Array.from(inputs).filter((input) => !input.validity.valid);
    const invalidSelects = Array.from(selects).filter((select) => !select.validity.valid);
    const invalidElements = [...invalidInputs, ...invalidSelects];
    invalidElements.forEach((invalidElement) => {
      invalidElement.reportValidity();
    });
    const cancelSubmit = invalidElements.length > 0;

    return cancelSubmit;
  };

  const getRecaptchaToken = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFormInvalid()) {
      return;
    }

    try {
      const token = await new Promise((resolve, reject) =>
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(props.recaptchaSiteKey, {
              action: 'Register'
            })
            .then(resolve)
            .catch(reject);
        })
      );
      recaptchaRef.current.value = token;
    } catch (error) {
      console.log('error', error);
    } finally {
      hiddenSubmitRef.current.closest('form').submit();
    }
  };

  useEffect(() => {
    if (state.passwordMismatch) return setState({ disableSubmit: true });

    return setState({ disableSubmit: false });
  }, [
    state.passwordConfirmation,
    state.password,
    state.passwordStrength,
    state.email,
    state.passwordMismatch
  ]);

  useEffect(() => {
    if (props.errors?.length > 0) {
      errorMessagesRef.current.focus();
    }
  }, [props.errors]);

  useEffect(() => {
    const countries = countryList
      .getNames()
      .map((country) => ({
        name: country,
        value: country
      }))
      .filter((country) => country.name !== 'United States of America')
      .filter((country) => country.name !== 'Åland Islands'); // There is no mapping for this country in Kentico so it needs to be removed
    const usStates = Object.keys(STATES).map((usStateKey) => ({
      name: STATES[usStateKey],
      value: usStateKey
    }));

    const usa = countryList.getName('US');
    const alphabeticalCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
    countries.unshift({ name: usa, value: usa }); // add USA to the top of the list

    if (props?.industries) {
      const industries = props.industries.map((industry) => ({
        name: industry,
        value: industry
      }));
      setIndustryOptions(industries);
    }
    setStateOptions(usStates);
    setCountryOptions(alphabeticalCountries);
  }, []);

  useEffect(() => {
    setState({
      email: props.email ? props.email : null,
      firstName: props.firstName,
      lastName: props.lastName,
      company: props.company,
      jobTitle: props.jobTitle,
      isAllianceClient: props.allianceMember,
      acceptedPrivacyPolicy: props.acceptedPrivacyPolicy,
      zipcode: props.zipCode
    });
    setSelectedCountry(props.country ? props.country : '');
    setSelectedState(props.state ? props.state : '');
    setSelectedIndustry(props.industry ? props.industry : '');
  }, []);

  const handleEnterKeySubmit = (e) => {
    if (e.submitter === hiddenSubmitRef.current) {
      e.preventDefault();
      e.stopPropagation();
      getRecaptchaToken(e);
    }
  };

  useEffect(() => {
    hiddenSubmitRef.current
      .closest('form')
      .addEventListener('submit', handleEnterKeySubmit, { once: true });
  }, []);

  return (
    <RegistrationStyledWrapper>
      <AccountForm useDefaultSubmitButton={false}>
        {props.errors?.length > 0 && (
          <ul className="error-messages" aria-live="polite" ref={errorMessagesRef}>
            {props.errors?.map((error, index) => (
              <li
                key={index}
                dangerouslySetInnerHTML={{
                  __html: error
                }}
              ></li>
            ))}
          </ul>
        )}
        <div className="form-fields-wrapper">
          {/* Email(username) Input */}

          <div className="form-field">
            <TextInput
              id="email"
              name="email"
              label={resourceStrings['bdO.Registration.EmailUsernameLabel']}
              errorMessages={{
                custom: state.emailError ? resourceStrings['bdO.Registration.error.email'] : ''
              }}
              type="email"
              onInput={handleEmailInput}
              value={state.email}
              required
            />
          </div>

          {/* First Name Input */}

          <div className="form-field">
            <TextInput
              id="firstName"
              name="firstName"
              label={resourceStrings['bdO.Registration.FirstNameLabel']}
              type="text"
              value={state.firstName}
              onInput={(e) => setState({ ...state, firstName: e.target.value })}
              required
            />
          </div>

          {/* Last Name Input */}

          <div className="form-field">
            <TextInput
              id="lastName"
              name="lastName"
              label={resourceStrings['bdO.Registration.LastNameLabel']}
              type="text"
              value={state.lastName}
              onInput={(e) => setState({ ...state, lastName: e.target.value })}
              required
            />
          </div>

          {/* Company Input */}

          <div className="form-field">
            <TextInput
              id="company"
              name="company"
              label={resourceStrings['bdO.Registration.CompanyLabel']}
              type="text"
              value={state.company}
              onInput={(e) => setState({ ...state, company: e.target.value })}
              required
            />
          </div>

          {/* Job Title Input */}

          <div className="form-field">
            <TextInput
              id="jobTitle"
              name="jobTitle"
              label={resourceStrings['bdO.Registration.JobTitleLabel']}
              type="text"
              value={state.jobTitle}
              onInput={(e) => setState({ ...state, jobTitle: e.target.value })}
              required
            />
          </div>

          {/* Country Input */}

          <div className="form-field">
            <FormSelect
              options={countryOptions}
              name="country"
              selectId="country"
              onChangeFunction={(e) => setSelectedCountry(e.target.value)}
              label={resourceStrings['bdO.Registration.CountryLabel']}
              placeholder={resourceStrings['bdO.Registration.CountryLabel']}
              selectedOption={selectedCountry}
              required
            />
          </div>

          {selectedCountry === 'United States of America' && (
            <>
              {/* State Input */}
              <div
                className={classNames('form-field', {
                  'visibility-none': selectedCountry !== 'United States of America'
                })}
              >
                <FormSelect
                  options={stateOptions}
                  name="state"
                  selectId="state"
                  onChangeFunction={(e) => setSelectedState(e.target.value)}
                  label={resourceStrings['bdO.Registration.StateLabel']}
                  placeholder={resourceStrings['bdO.Registration.StateLabel']}
                  selectedOption={selectedState}
                  required
                />
              </div>
              {/* Zipcode Input */}

              <div className="form-field">
                <TextInput
                  id="zipCode"
                  name="zipCode"
                  label={resourceStrings['bdO.Registration.ZipcodeLabel']}
                  type="text"
                  value={state.zipcode}
                  onInput={handleZipCodeInput}
                  onBlur={handleZipCodeInput}
                  errorMessages={{
                    custom: state.zipCodeError
                      ? resourceStrings['bdO.Registration.error.zipcode']
                      : ''
                  }}
                  pattern="[\d]{5}"
                  maxLength={5}
                  required
                />
              </div>
            </>
          )}

          {/* Industry Input */}

          <div className="form-field">
            <FormSelect
              options={industryOptions}
              name="industry"
              selectId="industry"
              onChangeFunction={(e) => setSelectedIndustry(e.target.value)}
              label={resourceStrings['bdO.Registration.IndustryLabel']}
              placeholder={resourceStrings['bdO.Registration.IndustryLabel']}
              selectedOption={selectedIndustry}
            />
          </div>

          {/* Password Policy */}

          <div className="password-policy" id="password-policy">
            <h2>{resourceStrings['bdO.PasswordPolicy-text']}</h2>
            <ul>
              {resourceStrings['bdO.PasswordPolicy-policies']?.split('-')?.map((policy) => (
                <li key={policy}>{policy}</li>
              ))}
            </ul>
            {props.errorMessage && (
              <p className="error-message">
                {props.errorMessage.split('\n').map((error) => (
                  <span key={error}>
                    {error} <br />
                  </span>
                ))}
              </p>
            )}
          </div>

          {/* Password Input */}

          <div className="password-inputs">
            <div className="form-field">
              <TextInput
                id="password"
                name="password"
                label={resourceStrings['bdO.PasswordReset.PasswordLabel']}
                type="password"
                errorMessages={{
                  custom: state.passwordMismatch
                    ? resourceStrings['bdO.PasswordPolicy.error.mismatch']
                    : ''
                }}
                additionalAriaLabel="password-strength password-policy"
                onInput={handlePassWordInputs}
                onBlur={handlePassWordInputs}
                value={state.password}
                required
              />
              {state.passwordStrength !== null &&
                state.password + state.passwordConfirmation !== '' && (
                  <p aria-live="polite" id="password-strength">
                    {resourceStrings['bdO.PasswordReset.PasswordStrength-text']}:{' '}
                    <b>{strengths[state.passwordStrength]}</b>
                  </p>
                )}
            </div>

            {/* Password Confirm Input */}

            <div className="form-field">
              <TextInput
                additionalAriaLabel="password-policy"
                id="passwordConfirmation"
                name="passwordConfirmation"
                label={resourceStrings['bdO.PasswordReset.PasswordConfirmLabel']}
                type="password"
                errorMessages={{
                  custom: state.passwordMismatch
                    ? resourceStrings['bdO.PasswordPolicy.error.mismatch']
                    : ''
                }}
                onInput={handlePassWordInputs}
                value={state.passwordConfirmation}
                required
              />
            </div>
          </div>

          {/* Is Alliance Client Input */}

          <div className="custom-checkbox">
            <input
              id="allianceMember"
              name="allianceMember"
              type="checkbox"
              value={state.isAllianceClient}
              onChange={() => setState({ ...state, isAllianceClient: !state.isAllianceClient })}
              checked={state.isAllianceClient}
            />
            <label htmlFor="allianceMember">
              {resourceStrings['bdO.Registration.IsAllianceClientLabel']}
            </label>
          </div>

          {/* Privacy Policy Input */}

          <div className="custom-checkbox">
            <input
              id="acceptedPrivacyPolicy"
              name="acceptedPrivacyPolicy"
              type="checkbox"
              value={state.acceptedPrivacyPolicy}
              onChange={() =>
                setState({ ...state, acceptedPrivacyPolicy: !state.acceptedPrivacyPolicy })
              }
              required
              checked={state.acceptedPrivacyPolicy}
            />
            <label
              htmlFor="acceptedPrivacyPolicy"
              dangerouslySetInnerHTML={{
                __html: resourceStrings['bdO.Registration.PrivacyPolicyLabel']
              }}
            ></label>
          </div>

          {/* Hidden Recaptcha field */}
          <input hidden id="gToken" name="gToken" type="text" ref={recaptchaRef} />

          <div className="form-field">
            <button
              disabled={state.disableSubmit}
              className="btn btn-primary submit"
              type="button"
              onClick={getRecaptchaToken}
            >
              {resourceStrings['bdO.Registration.SubmitButtonText']}
            </button>
          </div>
          <button
            ref={hiddenSubmitRef}
            hidden
            className="hidden btn btn-primary submit"
            type="submit"
          ></button>
        </div>
      </AccountForm>
    </RegistrationStyledWrapper>
  );
};

export default compose(withLocalization, withServerStyles)(RegistrationComponent);
